import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMe } from '../../hooks/useMe'
import { ApiClient } from '../../api'

// AdminDiagnostics - the original foundation-slice diagnostics panel, moved
// out of Dashboard.tsx and gated behind admin + collapsed by default. Kept
// around because it's an easy way to sanity-check auth/SQL/group claims when
// debugging in production. Never rendered for non-admins.

export function AdminDiagnostics() {
  const { data: me } = useMe()
  const [open, setOpen] = useState(false)
  const diag = useQuery({
    queryKey: ['diag'],
    queryFn: ApiClient.diag,
    enabled: open && !!me?.isAdmin
  })

  if (!me?.isAdmin) return null

  return (
    <section className="bg-white border border-line rounded">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 flex items-center justify-between text-sm font-semibold text-ink-soft hover:text-ink transition-colors"
      >
        <span>Admin diagnostics</span>
        <span aria-hidden className="text-xs font-mono">
          {open ? '▼' : '▶'}
        </span>
      </button>
      {open && (
        <div className="px-5 py-4 border-t border-line space-y-4 text-sm">
          <dl className="space-y-1.5 font-mono">
            <Row k="Authenticated">{me.isAuthenticated ? 'yes' : 'no'}</Row>
            <Row k="UPN">{me.upn}</Row>
            <Row k="User ID"><span className="text-xs">{me.userId}</span></Row>
            <Row k="DB user ID">{me.dbUserId ?? '(none)'}</Row>
            <Row k="Role">{me.role ?? '(none)'}</Row>
            <Row k="Admin">{me.isAdmin ? 'yes' : 'no'}</Row>
            <Row k="Financial">{me.hasFinancial ? 'yes' : 'no'}</Row>
            <Row k="Groups"><span className="text-xs break-all">{me.groups.join(', ')}</span></Row>
          </dl>
          {diag.isLoading && <div className="text-xs text-ink-soft">Loading /api/diag…</div>}
          {diag.error && (
            <pre className="text-xs text-oxblood whitespace-pre-wrap">
              {(diag.error as Error).message}
            </pre>
          )}
          {diag.data && (
            <pre className="text-xs bg-parchment p-3 rounded overflow-auto">
              {JSON.stringify(diag.data, null, 2)}
            </pre>
          )}
        </div>
      )}
    </section>
  )
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex">
      <dt className="w-32 text-ink-soft flex-shrink-0">{k}</dt>
      <dd className="flex-1">{children}</dd>
    </div>
  )
}
